import { useEffect, useState } from "react";
import { Form, Button, Row, Col, Card, Container, Spinner, Alert } from "react-bootstrap";
import { authFetch } from "@/lib/auth-fetch";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUser } from "@/app/usercontext";
import { TherapyFormFields, TherapyFormValues, useTherapyOptions } from "./therapy-form-fields";

interface Props {
    mode: "session" | "template";
}

interface FormData extends TherapyFormValues {
    date: string;
    dayOfWeek: string;
    time: string;
    notes: string;
}

export function TherapySessionForm({ mode }: Props) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const user = useUser();
    const isSession = mode === "session";

    const { options, loading: optionsLoading, error: optionsError } = useTherapyOptions(isEdit);

    const [formData, setFormData] = useState<FormData>({
        therapyType: "",
        clients: [""],
        date: new Date().toISOString().split("T")[0],
        dayOfWeek: "MONDAY",
        time: "",
        therapist: "",
        helper1: "",
        helper2: "",
        horse: "",
        notes: "",
        paidTherapist: true,
    });

    const [me, setMe] = useState<{ id: number; name: string; role: string } | null>(null);
    const [dataLoading, setDataLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // ADMIN guard for templates
    useEffect(() => {
        if (!isSession && user && user.role !== "ADMIN") {
            setError("Nur Administratoren können Vorlagen bearbeiten.");
            setDataLoading(false);
        }
    }, [isSession, user]);

    // Fetch auth/me (sessions) and edit data
    useEffect(() => {
        if (!isSession && user && user.role !== "ADMIN") return;

        let mounted = true;
        const fetches: Promise<any>[] = [];

        // Sessions need /auth/me for therapist auto-select
        if (isSession) {
            fetches.push(
                authFetch("/auth/me").then(res => {
                    if (!res.ok) throw new Error("Kein Benutzerprofil gefunden");
                    return res.json();
                })
            );
        } else {
            fetches.push(Promise.resolve(null));
        }

        // Edit mode: fetch existing entity
        if (isEdit) {
            const endpoint = isSession ? `/api/sessions/${id}` : `/api/session-templates/${id}`;
            const notFoundMsg = isSession ? "Sitzung nicht gefunden" : "Vorlage nicht gefunden";
            fetches.push(
                authFetch(endpoint).then(res => {
                    if (!res.ok) throw new Error(notFoundMsg);
                    return res.json();
                })
            );
        } else {
            fetches.push(Promise.resolve(null));
        }

        Promise.all(fetches)
            .then(([meData, entityData]) => {
                if (!mounted) return;

                if (isSession) {
                    setMe(meData);
                    if (isEdit && meData?.role !== "ADMIN") {
                        setError("Nur Administratoren können Sitzungen bearbeiten.");
                        setDataLoading(false);
                        return;
                    }
                }

                const todayStr = new Date().toISOString().split("T")[0];
                const base: FormData = {
                    therapyType: "",
                    clients: [""],
                    date: todayStr,
                    dayOfWeek: "MONDAY",
                    time: "",
                    therapist: "",
                    helper1: "",
                    helper2: "",
                    horse: "",
                    notes: "",
                    paidTherapist: true,
                };

                let next = { ...base };

                if (isSession) {
                    // Auto-select therapist from role
                    const therapistIdStr = meData?.id ? String(meData.id) : "";
                    if (meData?.role === "THERAPIST") {
                        next.therapist = therapistIdStr;
                    }

                    // Apply prefill from week-plan
                    const state = location?.state as any;
                    const prefill = state?.prefill as Record<string, any> | undefined;
                    if (prefill) {
                        next.therapyType = prefill.therapyType ?? next.therapyType;
                        next.date = prefill.date ?? next.date;
                        next.time = prefill.time ?? next.time;
                        next.therapist = prefill.therapist ?? next.therapist;
                        next.helper1 = prefill.helper1 ?? next.helper1;
                        next.helper2 = prefill.helper2 ?? next.helper2;
                        next.horse = prefill.horse ?? next.horse;
                        next.notes = prefill.notes ?? next.notes;
                        next.paidTherapist = prefill.paidTherapist ?? next.paidTherapist;
                        if (prefill.clients && prefill.clients.length > 0) {
                            next.clients = prefill.clients;
                        }
                    }

                    // Apply session data (edit)
                    if (entityData) {
                        next.therapyType = entityData.therapyType || next.therapyType;
                        next.clients = (entityData.clients && entityData.clients.length > 0
                            ? entityData.clients : [""]).map((v: number | string) => String(v));
                        next.date = entityData.date || next.date;
                        next.time = entityData.time || next.time;
                        next.therapist = entityData.therapist ? String(entityData.therapist) : next.therapist;
                        next.helper1 = entityData.helper1 ? String(entityData.helper1) : next.helper1;
                        next.helper2 = entityData.helper2 ? String(entityData.helper2) : next.helper2;
                        next.horse = entityData.horse ? String(entityData.horse) : next.horse;
                        next.notes = entityData.notes || next.notes;
                        next.paidTherapist = entityData.paidTherapist !== undefined ? entityData.paidTherapist : next.paidTherapist;
                    }
                } else {
                    // Template edit data
                    if (entityData) {
                        next.dayOfWeek = entityData.dayOfWeek || "MONDAY";
                        next.time = entityData.time || "";
                        next.therapyType = entityData.therapyType || "";
                        next.clients = (entityData.clientIds && entityData.clientIds.length > 0
                            ? entityData.clientIds : [""]).map((v: any) => String(v));
                        next.therapist = entityData.therapistId ? String(entityData.therapistId) : "";
                        next.helper1 = entityData.helper1Id ? String(entityData.helper1Id) : "";
                        next.helper2 = entityData.helper2Id ? String(entityData.helper2Id) : "";
                        next.horse = entityData.horseId ? String(entityData.horseId) : "";
                        next.paidTherapist = entityData.paidTherapist !== undefined ? entityData.paidTherapist : true;
                        next.notes = entityData.notes || "";
                    }
                }

                setFormData(next);
                setDataLoading(false);
            })
            .catch(e => {
                if (mounted) {
                    setError(e.message || "Fehler beim Laden");
                    setDataLoading(false);
                }
            });

        return () => { mounted = false; };
    }, [id, isEdit, isSession, user]);

    const handleTherapyFieldsChange = (values: TherapyFormValues) => {
        setFormData({ ...formData, ...values });
    };

    const goBack = () => {
        const state = location?.state as any;
        const from = state?.from as string | undefined;
        if (from) {
            navigate(from);
        } else {
            navigate(isSession ? "/sessions" : "/week-plan");
        }
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            let payload: any;
            let url: string;

            if (isSession) {
                payload = {
                    therapyType: formData.therapyType,
                    clients: formData.clients.filter(Boolean).map(v => Number(v)),
                    date: formData.date,
                    time: formData.time,
                    therapist: formData.therapist ? Number(formData.therapist) : null,
                    helper1: formData.helper1 ? Number(formData.helper1) : null,
                    helper2: formData.helper2 ? Number(formData.helper2) : null,
                    horse: formData.horse ? Number(formData.horse) : null,
                    notes: formData.notes || null,
                    paidTherapist: formData.paidTherapist,
                };
                url = isEdit ? `/api/sessions/${id}` : "/api/sessions";
            } else {
                payload = {
                    dayOfWeek: formData.dayOfWeek,
                    time: formData.time,
                    therapyType: formData.therapyType,
                    clientIds: formData.clients.filter(Boolean).map(v => Number(v)),
                    therapistId: formData.therapist ? Number(formData.therapist) : null,
                    helper1Id: formData.helper1 ? Number(formData.helper1) : null,
                    helper2Id: formData.helper2 ? Number(formData.helper2) : null,
                    horseId: formData.horse ? Number(formData.horse) : null,
                    paidTherapist: formData.paidTherapist,
                    notes: formData.notes || null,
                };
                url = isEdit ? `/api/session-templates/${id}` : "/api/session-templates";
            }

            const method = isEdit ? "PUT" : "POST";
            const res = await authFetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`Fehler beim Speichern (${res.status})`);

            if (isSession) {
                setSuccess("Sitzung gespeichert.");
            }
            goBack();
        } catch (err: any) {
            setError(err.message || "Unbekannter Fehler");
        } finally {
            setSaving(false);
        }
    };

    const loading = optionsLoading || dataLoading;

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    const title = isSession
        ? (isEdit ? "Sitzung bearbeiten" : "Neue Sitzung")
        : (isEdit ? "Vorlage bearbeiten" : "Neue Vorlage");

    const therapistDisabled = isSession && me?.role === "THERAPIST";
    const canSubmit = isSession ? (!isEdit || me?.role === "ADMIN") : true;

    return (
        <Container>
            <Row className="justify-content-center mt-4">
                <Col xs={12} md={10} lg={8}>
                    <Card>
                        <Card.Body>
                            <Card.Title>{title}</Card.Title>
                            {(error || optionsError) && <Alert variant="danger">{error || optionsError}</Alert>}
                            {success && <Alert variant="success">{success}</Alert>}
                            <Form onSubmit={onSubmit}>
                                {!isSession && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Wochentag</Form.Label>
                                        <Form.Select
                                            value={formData.dayOfWeek}
                                            required
                                            onChange={e => setFormData({ ...formData, dayOfWeek: e.target.value })}
                                        >
                                            <option value="MONDAY">Montag</option>
                                            <option value="TUESDAY">Dienstag</option>
                                            <option value="WEDNESDAY">Mittwoch</option>
                                            <option value="THURSDAY">Donnerstag</option>
                                            <option value="FRIDAY">Freitag</option>
                                            <option value="SATURDAY">Samstag</option>
                                            <option value="SUNDAY">Sonntag</option>
                                        </Form.Select>
                                    </Form.Group>
                                )}

                                {!isSession && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Uhrzeit</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={formData.time}
                                            required
                                            onChange={e => setFormData({ ...formData, time: e.target.value })}
                                        />
                                    </Form.Group>
                                )}

                                <TherapyFormFields
                                    options={options}
                                    values={formData}
                                    onChange={handleTherapyFieldsChange}
                                    therapistDisabled={therapistDisabled}
                                />

                                {isSession && (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Datum</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={formData.date}
                                                    required
                                                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Zeit</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={formData.time}
                                                    required
                                                    step={900}
                                                    onChange={e => setFormData({ ...formData, time: e.target.value })}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>Bemerkung</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={formData.notes}
                                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                    />
                                </Form.Group>

                                <Row>
                                    <Col className="d-flex justify-content-between">
                                        <Button variant="secondary" type="button" onClick={goBack}>Abbrechen</Button>
                                        {canSubmit && (
                                            <Button variant="primary" type="submit" disabled={saving}>
                                                {saving ? "Speichert..." : "Speichern"}
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
